import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Box, Button, CircularProgress } from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StarsIcon from '@mui/icons-material/Stars';
import ProductCardList from '../ProductCard/ProductCardList';
import ProductCardGrid from '../ProductCard/ProductCardGrid';
import PaginationControls from '../pagination/PaginationControls';
import { getCarritoByID, updateCarritoByID, deleteCarritoByID } from "../../services/CarritoServices";
import { getFavoritosByUserID, postFavorito, deleteFavorito } from "../../services/FavoritosServices";
import { AuthContext } from "../../context/authContext";

export const ProductList = ({
  products,
  isCheckStock,
  viewMode,
  totalPages,
  currentPage,
  perPage,
  totalItems,
  handlePageChange,
  handlePerPageChange,
  origin,
  handleRefreshFavorito
}) => {
  const { addToCart, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [favoritosItems, setFavoritosItems] = useState([]);
  const [refreshCarrito, setRefreshCarrito] = useState(0);
  const [loadingCart, setLoadingCart] = useState(false);
  const [loadingFav, setLoadingFav] = useState(false);
  const [quantities, setQuantities] = useState({});

  const isLogin = !!user;

  const clienteSelected = JSON.parse(localStorage.getItem('clienteSelected'));
  const userId = clienteSelected?.id ? clienteSelected?.id : user?.id;
  // Obtener carrito inicial
  const fetchCarrito = useCallback(async () => {
    const idCarrito = localStorage.getItem("idCarrito");
    if (!idCarrito) return;

    try {
      const res = await getCarritoByID(idCarrito);
      if (res?.Carrito_items) {
        const map = res.Carrito_items.map(item => ({
          cartId: res.id,
          itemId: item.id,
          artId: item.art_Id,
          cant: item.cantidad
        }));
        setCartItems(map);

        const qtyObj = {};
        map.forEach(i => qtyObj[i.artId] = i.cant);
        setQuantities(qtyObj);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Obtener favoritos del usuario
  const fetchFavoritos = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await getFavoritosByUserID(userId);
      const map = res?.map(item => ({
        favId: item.id,
        artId: item.art_Id,
        cliId: item.user_id,
      })) || [];
      setFavoritosItems(map);
    } catch (error) {
      console.error("Error al obtener favoritos:", error);
    }
  }, [userId]);

  // Cargar carrito y favoritos cuando cambia usuario o refreshCarrito
  useEffect(() => {
    if (userId) {
      fetchFavoritos();
      fetchCarrito();
    }
  }, [user, fetchFavoritos, fetchCarrito, refreshCarrito]);

  // Cambiar cantidad localmente y actualizar backend
  const handleQtyChange = async (idItem, newQty, productId) => {
    setQuantities(prev => ({ ...prev, [productId]: newQty }));
    if (!idItem) return;

    setLoadingCart(true);
    try {
      if (newQty > 0) {
        await updateCarritoByID(idItem, { cantidad: newQty, observacion: "" });
      } else {
        await deleteCarritoByID(idItem);
      }
      setRefreshCarrito(prev => prev + 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCart(false);
    }
  };

  // Agregar producto al carrito
  const handleAddToCart = async (productId, qtyFromControl) => {
    const qty = qtyFromControl || quantities[productId] || 1;
    setLoadingCart(true);
    try {
      await addToCart(productId, qty,0, "");
      setRefreshCarrito(prev => prev + 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCart(false);
    }
  };

  // Eliminar producto del carrito
  const handleDeleteToCart = async (idItem) => {
    if (!idItem) return;
    setLoadingCart(true);
    try {
      await deleteCarritoByID(idItem);
      setRefreshCarrito(prev => prev + 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCart(false);
    }
  };

  // Agregar producto a favoritos
  const handleAddFavorito = async (productId) => {
    setLoadingFav(true);
    try {
      await postFavorito({ art_Id: productId, user_id: userId });
      if (origin) handleRefreshFavorito();
      await fetchFavoritos();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFav(false);
    }
  };

  // Eliminar producto de favoritos
  const handleDeleteFavorito = async (idFavorito) => {
    setLoadingFav(true);
    try {
      await deleteFavorito(idFavorito);
      if (origin) handleRefreshFavorito();
      await fetchFavoritos();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFav(false);
    }
  };

  // Navegar a detalle de producto
  const handleClickTitle = (product) => {
    const itemInCart = cartItems.find(item => item.artId === product.id);
    const idItem = itemInCart?.itemId || null;
    const cantItem = quantities[product.id] || itemInCart?.cant || 1;
    const itemInFav = favoritosItems.find(
      item => item.artId === product.id && item.cliId === userId
    );

    navigate(`/product/${product.id}`, {
      state: {
        productData: {
          ...product,
          urls: [product.imgSrc],
          idItem,
          cantItem,
          isFav: !!itemInFav
        }
      },
    });
  };


  // Navegar a categoría cuando carrito vacío
  const handleClickEmpty = () => navigate("/category");

  return (
    <>
      {viewMode === "horizontal" ? (
        products.length > 0 ? (
          products.map((product) => {
            const itemInCart = cartItems.find(item => item.artId === product.id);
            const idItem = itemInCart?.itemId || null;
            const cantItem = quantities[product.id] || itemInCart?.cant || 1;
            const itemInFav = favoritosItems.find(item => item.artId === product.id && item.cliId === userId);
 
            return (
              <Grid container item key={product.id} spacing={1}>
                <Grid item xs={12}>
                  <ProductCardList
                    {...product}
                    isLogin={isLogin}
                    isCheckStock={isCheckStock}
                    isStockAvailable={product.stockActual > 0}
                    stockLimit={product.stockActual}
                    isStockFractional={product.fractional}
                    isInCart={!!itemInCart}
                    idItem={idItem}
                    cantItem={cantItem}
                    isInFav={!!itemInFav}
                    idFav={itemInFav?.favId || null}
                    onQtyChange={(id, qty) => handleQtyChange(id, qty, product.id)}
                    loading={loadingCart}
                    handleCartAdd={(qty) => handleAddToCart(product.id, qty)}
                    handleCartDelete={() => handleDeleteToCart(idItem)}
                    handleClickTitle={() => handleClickTitle(product)}
                    handleAddFavorito={handleAddFavorito}
                    handleDeleteFavorito={handleDeleteFavorito}
                  />
                </Grid>
              </Grid>
            );
          })
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" textAlign="center" width="100%" maxWidth={400} margin="0 auto">
            {origin ?
              <>
                <StarsIcon style={{ fontSize: 80, color: "#ccc", marginBottom: 16 }} />

                <Typography variant="h5" color="text.secondary" gutterBottom>
                  Tu lista está vacía
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  ¿Por qué sigues explorando nuestros artículos?
                </Typography>
              </>
              :
              <>
                <ShoppingCartOutlinedIcon style={{ fontSize: 80, color: "#ccc", marginBottom: 16 }} />

                <Typography variant="h5" color="text.secondary" gutterBottom>
                  Tu carrito está vacío
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  No se encontraron productos. ¿Por qué no exploras nuestros artículos?
                </Typography>
              </>
            }

            <Button variant="contained" color="primary" onClick={handleClickEmpty}>
              Ver productos
            </Button>
          </Box>
        )
      ) : (
        <Grid container spacing={2} sx={{ padding: "2%" }}>
          {products.length > 0 ? (
            products.map((product) => {
              const itemInCart = cartItems.find(item => item.artId === product.id);
              const idItem = itemInCart?.itemId || null;
              const cantItem = quantities[product.id] || itemInCart?.cant || 1;
              const itemInFav = favoritosItems.find(item => item.artId === product.id && item.cliId === userId);
 
              return (
                <Grid container item xs={6} sm={4} md={3} lg={2} key={product.id} spacing={1}>
                  <Grid item xs={12}>
                    <ProductCardGrid
                      {...product}
                      isLogin={isLogin}
                      isCheckStock={isCheckStock}
                      isStockAvailable={product.stockActual > 0}
                      stockLimit={product.stockActual}
                      isStockFractional={product.fractional}
                      isInCart={!!itemInCart}
                      idItem={idItem}
                      cantItem={cantItem}
                      isInFav={!!itemInFav}
                      idFav={itemInFav?.favId || null}
                      onQtyChange={(id, qty) => handleQtyChange(id, qty, product.id)}
                      loading={loadingCart}
                      handleCartAdd={(qty) => handleAddToCart(product.id, qty)}
                      handleCartDelete={() => handleDeleteToCart(idItem)}
                      handleClickTitle={() => handleClickTitle(product)}
                      handleAddFavorito={handleAddFavorito}
                      handleDeleteFavorito={handleDeleteFavorito}
                    />
                  </Grid>
                </Grid>
              );
            })
          ) : (
            <Typography align="center" sx={{ width: "100%", mt: 5 }}>
              No se encontraron productos.
            </Typography>
          )}
        </Grid>
      )}

      {products.length > 0 && (
        <PaginationControls
          totalPages={totalPages}
          currentPage={currentPage}
          perPage={perPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      )}
    </>
  );
};

export default ProductList;
